import * as React from 'react'
import makeStyles from '@material-ui/core/styles/makeStyles'
import clsx from 'clsx'
import Image from 'next/image'
import Divider from '@material-ui/core/Divider'
import RouterLink from 'containers/RouterLink'
import UserSettingsLayout from 'containers/UserSettingsLayout'
import { useUser } from 'api'
import { useTranslations } from 'containers/Translations/TranslationsContext'
import ComposedAccordion from 'components/ComposedAccordion'
import Paper from 'components/Paper'
import Button from 'components/Button'
import Typography from 'components/Typography'
import Section from 'components/Section'
import { formatDate } from 'utils/formatters'

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    '& > *': {
      width: '100%',
    },
  },
  summary: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  summaryText: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  accordion: {
    '& > span': {
      width: '100%',
      padding: theme.spacing(2.5, 6, 2.5, 0),
    },
  },
  trackOrder: {
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(1.3, 8),
    },
    fontSize: '12px',
    fontWeight: 500,
  },
  totalSubtitle: {
    color: '#837F8A',
    fontWeight: 400,
  },
  ordersTitle: {
    marginBottom: theme.spacing(7),
  },
  expandedAccordion: {
    gap: theme.spacing(4),
  },
  orderDetails: {
    gap: theme.spacing(2),
  },
  column: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  paper: {
    padding: theme.spacing(4),
    gap: theme.spacing(2),
  },
  detailsKey: {
    color: '#837F8A',
    textTransform: 'uppercase',
  },
  details: {
    textTransform: 'none',
  },
  productDetails: {
    padding: theme.spacing(2, 4),
    width: '100%',
  },
  product: {
    display: 'grid',
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: '170px 1fr',
    },
    gap: theme.spacing(2),
    width: 'inherit',
  },
  productTitleImage: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    '& > div > img': {
      objectFit: 'contain',
    },
  },
  productInfo: {
    alignItems: 'flex-end',
    [theme.breakpoints.up('md')]: {
      height: 'calc(100% - 39px)',
    },
    alignSelf: 'flex-end',
    justifyContent: 'space-between',
  },
  quantityPrice: {
    alignItems: 'flex-end',
  },
  productDetailsContainer: {
    gap: theme.spacing(2),
  },
  divider: {
    marginBottom: theme.spacing(4),
  },
}))

const MyOrders = (props) => {
  const { onGetOrder, orderHistory } = useUser()

  React.useEffect(() => {
    onGetOrder()
    return () => {}
  }, [onGetOrder])

  const t = useTranslations()
  const classes = useStyles(props)

  return (
    <UserSettingsLayout>
      <Section className={classes.root}>
        <Typography component="h1" variant="h6" className={classes.ordersTitle}>
          {t('Web.MyOrders.Title')}
        </Typography>

        {orderHistory.length < 1 ? (
          <>
            <Divider className={classes.divider} />
            <Typography component="h1" variant="body1">
              {t('Web.MyOrders.NoOrders')}
            </Typography>
          </>
        ) : (
          orderHistory?.map((order) => (
            <ComposedAccordion
              key={order?.id}
              summary={
                <div className={classes.summary}>
                  <div className={classes.summaryText}>
                    <Typography component="p" variant="subtitle1">
                      {t('Web.MyOrders.OrderNo')} {order?.id}
                    </Typography>
                    <Typography component="p" variant="subtitle2" className={classes.totalSubtitle}>
                      {t('Web.MyOrders.Total')}
                      {`${order?.orderSales} ${order.marketCurrencySign}`}
                    </Typography>
                  </div>

                  {order?.deliveryTrackingUrl ? (
                    <Button
                      variant="outlined"
                      color="primary"
                      className={classes.trackOrder}
                      component={RouterLink}
                      href={order?.deliveryTrackingUrl}
                    >
                      {t('Web.MyOrders.TrackOrder')}
                    </Button>
                  ) : (
                    <Button variant="outlined" color="primary" className={classes.trackOrder}>
                      {t('Web.MyOrders.TrackOrder')}
                    </Button>
                  )}
                </div>
              }
              classes={{ summaryContent: classes.accordion }}
              disableGutters
            >
              <div className={clsx(classes.column, classes.expandedAccordion)}>
                <div className={clsx(classes.column, classes.orderDetails)}>
                  <Typography component="p" variant="subtitle1">
                    {t('Web.MyOrders.OrderDetails')}
                  </Typography>
                  <Paper className={clsx(classes.column, classes.paper)}>
                    <Typography component="p" variant="subtitle1" className={classes.details}>
                      <span className={classes.detailsKey}>
                        {t('Web.MyOrders.DeliveryAddress')}
                      </span>
                      {order?.shipment?.shipmentAddress?.addressLine1 ||
                        order?.shipment?.shipmentAddress?.addressLine2 ||
                        ''}
                    </Typography>

                    <Typography component="p" variant="subtitle1" className={classes.details}>
                      <span className={classes.detailsKey}>{t('Web.MyOrders.DeliveryOption')}</span>
                      {order?.shipment?.shipmentMethod?.type || ''}
                    </Typography>

                    <Typography component="p" variant="subtitle1" className={classes.details}>
                      <span className={classes.detailsKey}> {t('Web.MyOrders.PaymentOption')}</span>
                      {order?.paymentsApplied?.[0]?.description || ''}
                    </Typography>

                    <Typography component="p" variant="subtitle1" className={classes.details}>
                      <span className={classes.detailsKey}> {t('Web.MyOrders.OrderStatus')}</span>
                      {order?.deliveryStatus || 'Completed'}
                    </Typography>
                  </Paper>
                </div>

                <div className={clsx(classes.column, classes.productDetailsContainer)}>
                  <Typography component="p" variant="subtitle1">
                    {t('Web.MyOrders.ProductDetails')}
                  </Typography>

                  {order?.orderItems?.map((item, idx) => (
                    <React.Fragment key={`${item?.productVariantName}${idx}`}>
                      {item?.lineType !== 'F' && (
                        <Paper className={classes.productDetails}>
                          <article className={classes.product}>
                            <div className={classes.productTitleImage}>
                              <Typography component="p" variant="subtitle2">
                                {item?.productVariantName}
                                <span className={classes.detailsKey}>
                                  {' '}
                                  {item?.productVariantNameSuffix}
                                </span>
                              </Typography>
                              <Image
                                src={item?.productImageUrl}
                                alt={item.productVariantName}
                                width={131}
                                height={224}
                              />
                            </div>

                            <div className={clsx(classes.column, classes.productInfo)}>
                              <Typography
                                component="p"
                                variant="subtitle2"
                                className={classes.totalSubtitle}
                              >
                                {t('Web.MyOrders.DateOrdered')}
                                {formatDate(order?.orderDate)}
                              </Typography>

                              <div>
                                <Typography
                                  component="p"
                                  variant="subtitle2"
                                  className={classes.totalSubtitle}
                                >
                                  {t('Web.MyOrders.Quantity')}
                                  {item.qtyReserved}
                                </Typography>
                                <Typography
                                  component="p"
                                  variant="subtitle2"
                                  className={clsx(classes.column, classes.quantityPrice)}
                                >
                                  {order?.orderCurrencySign
                                    ? `${item?.orderPrice}${order?.orderCurrencySign}`
                                    : `${item?.orderDisplayPriceTotal}`}
                                </Typography>
                              </div>
                            </div>
                          </article>
                        </Paper>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </ComposedAccordion>
          ))
        )}
      </Section>
    </UserSettingsLayout>
  )
}

MyOrders.propTypes = {}

export default MyOrders
